import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

/**
 * Polls /community/notifications every 30s for:
 *   - comments on the current user's posts
 *   - police pins dropped within 1km of the current device location
 * Shows a toast for each new event and bumps a `since` timestamp so the same
 * event never re-toasts. Renders nothing.
 *
 * Mounted globally inside <AuthProvider> so it follows the user across routes.
 */
export default function CommunityNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const sinceRef = useRef(null);
  const locRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  // Lazily try to read GPS once (no aggressive watch — battery friendly).
  useEffect(() => {
    if (!user) return;
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { locRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude }; },
      () => { /* silent */ },
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 }
    );
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const params = new URLSearchParams();
        if (sinceRef.current) params.set("since", sinceRef.current);
        if (locRef.current?.lat != null) {
          params.set("lat", locRef.current.lat);
          params.set("lng", locRef.current.lng);
        }
        const { data } = await api.get(`/community/notifications?${params.toString()}`);
        if (cancelled) return;

        // First boot: don't spam with the last 10 minutes of backlog — just
        // sync the cursor and exit.
        if (sinceRef.current == null) {
          sinceRef.current = data.now;
          return;
        }

        for (const c of data.new_comments || []) {
          if (seenIdsRef.current.has(c.comment_id)) continue;
          seenIdsRef.current.add(c.comment_id);
          toast.message(`${c.user_name} commented on your post`, {
            description: c.text?.slice(0, 90),
            action: { label: "View", onClick: () => navigate("/community") },
            duration: 7000,
          });
        }
        for (const p of data.nearby_police || []) {
          if (seenIdsRef.current.has(p.post_id)) continue;
          seenIdsRef.current.add(p.post_id);
          toast.warning(`🚓 Cops nearby — ${p.distance_km} km away`, {
            description: p.text || p.location?.address || "Drive carefully",
            action: { label: "Map", onClick: () => navigate("/community") },
            duration: 10000,
          });
        }
        sinceRef.current = data.now;
      } catch { /* silent — network blip */ }
    };

    poll();
    const id = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.user_id, navigate]);

  return null;
}
